import { ZoomTranscriptView } from '../../../../../extViewmodels/sdk/models/Zoom';
import { ZoomMeetingView } from '../../../../../models/Zoom';
import { ILeadSearchView } from '../../../../viewModels/leads/interfaces';

interface RecordingAndTranscript {
	recording: ZoomMeetingView;
	transcript?: ZoomTranscriptView;
}

/**
 * Extracts and formats demo context information from lead search view and recordings data
 * Returns a formatted string ready for AI analysis with separate sections for each recording
 * Returns null if no transcript content is available
 */
export const extractDemoContext = (
	leadSearchView: ILeadSearchView,
	recordingAndTranscriptions: RecordingAndTranscript[]
): string | null => {
	// Check if we have any actual transcript content before proceeding
	const hasTranscriptContent = recordingAndTranscriptions.some(item => item.transcript?.transcriptText);
	if (!hasTranscriptContent) {
		return null;
	}
	const { company, nextMeeting, lastDeal, lastInteraction } = leadSearchView;
	const parts: string[] = [];

	// Company/Demo metadata (shared across all recordings)
	if (company?.companyName) {
		parts.push(`Company: ${company.companyName}`);
	}

	// Extract company vertical from tags
	const companyVertical = company?.tags?.find((tag: string) => tag.includes('Vertical:'))?.replace('Vertical: ', '');
	if (companyVertical) {
		parts.push(`Industry: ${companyVertical}`);
	}

	// Extract company size indicators from tags
	const companySizeTag = company?.tags?.find((tag: string) => tag.includes('SIZE:') || tag.includes('EMPLOYEES:'));
	if (companySizeTag) {
		parts.push(`Company Size: ${companySizeTag}`);
	}

	if (company?.timeZone) {
		parts.push(`Time Zone: ${company.timeZone}`);
	}

	// Format closer name from lastDeal creator
	const closerName = lastDeal?.creator
		? `${lastDeal.creator.firstName} ${lastDeal.creator.lastName}`.trim()
		: undefined;
	if (closerName) {
		parts.push(`Closer: ${closerName}`);
	}

	// Demo outcome
	if (lastDeal?.stage) {
		parts.push(`Deal Stage: ${lastDeal.stage}`);
	}
	if (lastInteraction?.outcome?.name) {
		parts.push(`Outcome: ${lastInteraction.outcome.name}`);
		if (lastInteraction.outcome.description) {
			parts.push(`Outcome Details: ${lastInteraction.outcome.description}`);
		}
	}
	if (nextMeeting?.confirmationAttempts !== undefined) {
		parts.push(`Confirmation Attempts: ${nextMeeting.confirmationAttempts}`);
	}

	// Timing - format demo date and time
	const demoPerformDate = lastDeal?.demoPerformDate || nextMeeting?.startDate;
	if (demoPerformDate) {
		const demoDate = new Date(demoPerformDate).toISOString().split('T')[0];
		const demoTime = new Date(demoPerformDate).toLocaleTimeString();
		parts.push(`Demo Date: ${demoDate}`);
		parts.push(`Demo Time: ${demoTime}`);
	}

	// Add a separator before recordings section
	if (recordingAndTranscriptions.length > 0) {
		parts.push(''); // Empty line
		parts.push('--- Recordings ---');
	}

	// Iterate through all recordings and create separate sections
	recordingAndTranscriptions.forEach((item, index) => {
		const { recording, transcript } = item;
		const recordingParts: string[] = [];

		recordingParts.push(`\nRecording ${index + 1}:`);

		// Meeting metrics from recording or transcript
		const duration = recording?.duration || transcript?.durationMinutes;
		if (duration) {
			recordingParts.push(`  Demo Duration: ${Math.round(duration)} minutes`);
		}

		const topic = recording?.topic || transcript?.topic;
		if (topic) {
			recordingParts.push(`  Meeting Topic: ${topic}`);
		}

		// Extract attendee information from recording
		let attendeeNames: string[] = [];
		let externalAttendeeCount = 0;

		if (recording) {
			// Internal participants
			if (recording.participants?.length > 0) {
				attendeeNames = recording.participants.map(p => p.name).filter(Boolean);
			}

			// External participants (prospects)
			if (recording.externalParticipants?.length > 0) {
				externalAttendeeCount = recording.externalParticipants.length;
				const externalNames = recording.externalParticipants.map(p => p.name).filter(Boolean);
				attendeeNames = [...attendeeNames, ...externalNames];
			}
		}

		if (externalAttendeeCount > 0) {
			recordingParts.push(`  Number of Prospects: ${externalAttendeeCount}`);
		}
		if (attendeeNames.length > 0) {
			recordingParts.push(`  Attendees: ${attendeeNames.join(', ')}`);
		}

		// Recording start time
		const recordingStartTime = recording?.startTime || transcript?.startTime;
		if (recordingStartTime) {
			const startDate = new Date(recordingStartTime).toISOString().split('T')[0];
			const startTime = new Date(recordingStartTime).toLocaleTimeString();
			recordingParts.push(`  Recording Date: ${startDate}`);
			recordingParts.push(`  Recording Time: ${startTime}`);
		}

		// Add transcript if available
		if (transcript?.transcriptText) {
			recordingParts.push(`  Transcript:\n${transcript.transcriptText}`);
		}

		parts.push(...recordingParts);
	});

	return parts.join('\n');
};
